import { computed } from 'vue'
import { useRouter, type HistoryState, type RouteLocationRaw, type Router } from 'vue-router'

const RETURN_STACK = 'pjReturnStack'
const MAX_RETURN_DEPTH = 20

/** Return destinations belong to a history entry, not a URL or a shared tab store. */
export function createBackNavigation(router: Router) {
  function workspaceId() {
    return String(router.currentRoute.value.params.workspaceId || '')
  }

  function isWorkspacePath(value: unknown, workspace: string): value is string {
    if (!workspace || typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//') || /[\\\u0000-\u001f]/.test(value)) return false
    const url = new URL(value, 'https://navigation.invalid')
    return url.origin === 'https://navigation.invalid' && url.pathname.startsWith(`/${encodeURIComponent(workspace)}/`)
  }

  function returnStack(workspace = workspaceId()): string[] {
    const value = router.options.history.state[RETURN_STACK]
    if (!Array.isArray(value)) return []
    // Discard an invalid chain together: never skip over a foreign workspace.
    return value.length <= MAX_RETURN_DEPTH && value.every(path => isWorkspacePath(path, workspace))
      ? [...value] as string[]
      : []
  }

  function targetWithStack(target: RouteLocationRaw, stack: string[]) {
    const resolved = router.resolve(target)
    const options = typeof target === 'string' ? {} : target
    return {
      path: resolved.path,
      query: resolved.query,
      hash: resolved.hash,
      replace: options.replace,
      force: options.force,
      state: { ...options.state, [RETURN_STACK]: stack } satisfies HistoryState,
    }
  }

  /** Open a child screen, preserving the parent's own return destination. */
  function withReturnTo(target: RouteLocationRaw, origin: RouteLocationRaw = router.currentRoute.value.fullPath) {
    const source = router.resolve(origin).fullPath
    const destination = router.resolve(target).fullPath
    const workspace = workspaceId()
    if (!isWorkspacePath(source, workspace) || !isWorkspacePath(destination, workspace)) {
      return targetWithStack(target, [])
    }
    const stack = returnStack()
    if (source === destination) return targetWithStack(target, stack)
    // Revisiting a parent unwinds the chain instead of creating a Back loop.
    const parent = stack.indexOf(destination)
    return targetWithStack(target, parent >= 0
      ? stack.slice(0, parent)
      : [...stack, source].slice(-MAX_RETURN_DEPTH))
  }

  /** Stay within the same editor/preview flow, including replacing a new draft URL. */
  function keepReturnTo(target: RouteLocationRaw) {
    const destination = router.resolve(target).fullPath
    return targetWithStack(target, isWorkspacePath(destination, workspaceId()) ? returnStack() : [])
  }

  const backTarget = computed(() => {
    // Reading currentRoute makes this update after push, replace and browser Back/Forward.
    const current = router.currentRoute.value.fullPath
    const stack = returnStack()
    while (stack[stack.length - 1] === current) stack.pop()
    const destination = stack[stack.length - 1]
    return destination ? targetWithStack(destination, stack.slice(0, -1)) : undefined
  })

  return { backTarget, withReturnTo, keepReturnTo }
}

export function useBackNavigation() {
  return createBackNavigation(useRouter())
}
